import { Injectable } from '@angular/core';
import { TreatmentTemplateService } from 'src/app/logged-in-navbar/clinic-setup-modal/treatment-template/treatment-template.service';
import { PatientProfile } from './models/patient.model';
import { AITreatmentTemplate, TreatmentRecommendation } from './models/treatment.model';

export interface Diagnosis {
  severity: string;
  category: string;
}

@Injectable({
  providedIn: 'root'
})
export class RecommendationService {
  constructor(private templateService: TreatmentTemplateService) {}

  generateRecommendations(profile: PatientProfile): TreatmentRecommendation {
    console.log('[RecommendationService] Received profile:', profile);

    const diagnosis = this.normalizeDiagnosisFromProfile(profile);
    const templateKey = this.getPhaseOneTemplateKey(diagnosis);

    const base = this.getTemplateByKeyOrUndefined(templateKey, diagnosis);
    const enhanced = base;
    const recommendation: TreatmentRecommendation = {
      baseTemplate: base || this.buildFallbackTemplate(diagnosis),
      enhancedTemplate: enhanced || this.buildFallbackTemplate(diagnosis),
      priority: this.calculatePriority(diagnosis),
      rationale: this.buildPhaseOneRationale(diagnosis, templateKey, !!base)
    };

    return recommendation;
  }

  private parsePrediction(predictionRaw: string): Diagnosis | null {
    if (!predictionRaw) return null;
    const parts = predictionRaw.trim().split(/\s+/);
    const severity = parts[0] && parts[0].toUpperCase();
    const category = parts[1] && parts[1].toUpperCase();
    if (!severity || !category) return null;
    return { severity, category };
  }

  private getBaseTemplate(diagnosis: Diagnosis): AITreatmentTemplate | undefined {
    const key = `${diagnosis.severity.toLowerCase()}-${diagnosis.category.toLowerCase()}-1`;
    console.log('[RecommendationService] Looking up template:', key);
    const template = this.templateService.getTemplateByKey(key);
    return template
      ? { ...template, category: diagnosis.category, severity: diagnosis.severity }
      : undefined;
  }

  private getMimickerTemplates(mimickers: string[]): AITreatmentTemplate[] {
    return mimickers
      .map(m => this.templateService.getTemplateByKey(m.replace(/\s+/g, '-').toLowerCase()))
      .filter((t): t is AITreatmentTemplate => !!t);
  }

  private getRiskFactorTemplates(riskFactors: string[]): AITreatmentTemplate[] {
    return riskFactors
      .map(r => this.templateService.getTemplateByKey(r.replace(/\s+/g, '-').toLowerCase()))
      .filter((t): t is AITreatmentTemplate => !!t);
  }

  private mergeTemplates(
    base?: AITreatmentTemplate,
    additions: AITreatmentTemplate[] = []
  ): AITreatmentTemplate | undefined {
    if (!base && !additions.length) return undefined;
    if (!base) return additions[0];

    const merged: AITreatmentTemplate = { ...base };
    const mergeArray = (a?: string[], b?: string[]) => [...new Set([...(a || []), ...(b || [])])];

    additions.forEach(add => {
      merged.otcEyeDrops = mergeArray(merged.otcEyeDrops, add.otcEyeDrops);
      merged.rxEyeDrops = mergeArray(merged.rxEyeDrops, add.rxEyeDrops);
      merged.procedures = mergeArray(merged.procedures, add.procedures);
      merged.exercises = mergeArray(merged.exercises, add.exercises);
    });

    return merged;
  }

  private calculatePriority(diagnosis: Diagnosis): 'HIGH' | 'MEDIUM' | 'LOW' {
    switch (diagnosis.severity) {
      case 'SEVERE':
        return 'HIGH';
      case 'MODERATE':
        return 'MEDIUM';
      default:
        return 'LOW';
    }
  }

  private generateRationale(
    diagnosis: Diagnosis,
    base?: AITreatmentTemplate,
    enhanced?: AITreatmentTemplate
  ): string[] {
    const rationale: string[] = [];
    if (base)
      rationale.push(`Base template derived from ${diagnosis.severity} ${diagnosis.category}.`);
    if (enhanced && enhanced !== base)
      rationale.push('Enhanced with mimicker and risk factor templates.');
    if (!base && !enhanced) rationale.push('No matching treatment template found.');
    return rationale;
  }

private normalizeDiagnosisFromProfile(profile: PatientProfile): Diagnosis {
  const diagnosis = profile && (profile as any).diagnosis ? (profile as any).diagnosis : null;

  const rawSeverity = ((diagnosis && diagnosis.severity) || 'MODERATE').toString();
  const rawCategory = ((diagnosis && diagnosis.category) || 'AQUEOUS').toString();

  const severity = this.normalizeSeverity(rawSeverity);
  const category = this.normalizeCategory(rawCategory);

  return { severity, category };
}

  private normalizeSeverity(severityRaw: string): 'MILD' | 'MODERATE' | 'SEVERE' {
    const s = severityRaw.trim().toUpperCase();
    if (s.startsWith('MILD')) return 'MILD';
    if (s.startsWith('SEV')) return 'SEVERE';
    if (s.startsWith('MOD')) return 'MODERATE';
    return 'MODERATE';
  }

  private normalizeCategory(categoryRaw: string): 'AQUEOUS' | 'EVAPORATIVE' | 'MIXED' {
    const c = categoryRaw.trim().toUpperCase();

    if (c.includes('MIX')) return 'MIXED';
    if (c.includes('EVAP')) return 'EVAPORATIVE';
    if (c.includes('AQUE')) return 'AQUEOUS';
    if (c.includes('LIPID')) return 'EVAPORATIVE';
    if (c.includes('AQUEOUS_DEFICIENT')) return 'AQUEOUS';

    return 'AQUEOUS';
  }

  private getPhaseOneTemplateKey(diagnosis: Diagnosis): string {
    const severitySlug = diagnosis.severity.toLowerCase();
    const categorySlug =
      diagnosis.category === 'AQUEOUS'
        ? 'aqueous'
        : diagnosis.category === 'EVAPORATIVE'
          ? 'evaporative'
          : 'mixed';

    return `${severitySlug}-${categorySlug}-1`;
  }

  private getTemplateByKeyOrUndefined(
    key: string,
    diagnosis: Diagnosis
  ): AITreatmentTemplate | undefined {
    const template = this.templateService.getTemplateByKey(key);
    if (!template) return undefined;

    return {
      ...template,
      severity: diagnosis.severity,
      category: diagnosis.category
    };
  }

  private buildFallbackTemplate(diagnosis: Diagnosis): AITreatmentTemplate {
    return {
      severity: diagnosis.severity,
      category: diagnosis.category,
      otcEyeDrops: [],
      rxEyeDrops: [],
      procedures: [],
      exercises: []
    };
  }

  private buildPhaseOneRationale(
    diagnosis: Diagnosis,
    templateKey: string,
    foundTemplate: boolean
  ): string[] {
    const base = `${diagnosis.severity.toLowerCase()} ${diagnosis.category.toLowerCase()}`.replace(
      /_/g,
      ' '
    );

    if (foundTemplate) {
      return [
        `AI impression mapped to ${base}.`,
        `Selected default template: ${templateKey}.`
      ];
    }

    return [
      `AI impression mapped to ${base}.`,
      `No default template found for ${templateKey}; generated an empty plan.`
    ];
  }
}